// §9.3 / §9.4 — the origin check.
//
// **The origin is the boundary.** §9.4 says so in as many words, and explains why: the frame carries
// `allow-same-origin` (external apps need their own cookies and storage), so the sandbox attribute
// is not containing anything. What contains an app is that the shell will only talk to, and only
// believe, its declared origin.
//
// This file is four lines of logic and exists on its own so that the comparison has exactly one
// implementation and one place to be reviewed.

/**
 * `MessageEvent.origin` compared against a manifest's `origin`, as strings, with no normalisation
 * beyond what the browser already does.
 *
 * No wildcards, no suffix matching, no "well, it is a subdomain": `https://apps.example` and
 * `https://evil.apps.example` are different origins and one may not speak for the other. A frame
 * with an opaque origin reports `"null"`, which never matches anything — a sandboxed frame without
 * `allow-same-origin` therefore cannot bridge, which is correct and is why §9.4 requires the flag.
 */
export function originMatches(eventOrigin: unknown, manifestOrigin: string): boolean {
  if (typeof eventOrigin !== 'string' || eventOrigin.length === 0) return false;
  if (eventOrigin === 'null') return false;
  return eventOrigin === manifestOrigin;
}

/** §9.4 — the sandbox flags every app frame carries. `allow-same-origin` is required. */
export const APP_FRAME_SANDBOX =
  'allow-scripts allow-forms allow-popups allow-downloads allow-same-origin';

/** §9.4 — the permissions policy the frame is loaded with. */
export const APP_FRAME_ALLOW = 'clipboard-write';
